<template lang="pug">
  div(class="dynamic-formula-wrapper")
    button(
      class="dynamic-formula__activator"
      :disabled="isDisabled"
      :class="formulaButtonClasses"
      @click.stop="onMenuOpen"
    )
      template(v-if="!toolbarsMap[field.id]")
        span(
          class="text-12px dark dynamic-formula__activator-text"
          :style="formulaTextStyles"
        ) {{ selectedFormulaText }}
        e-svg-icon(size="sm") formula-gray
      div(
        v-else
        class="dynamic-formula__body"
      )
        v-menu(
          offset-x
          nudge-right="18"
        )
          template(v-slot:activator="{ on }")
            button(
              class="dynamic-formula__menu-btn text-12px light"
              v-on="on"
            ) {{ selectFormulaButtonText }}
              |
              e-svg-icon arrow-right-gray
          v-list(
            dense
            class="menu-list sm py-0"
          )
            v-list-item(
              class="menu-list__item sm"
              :class="{ selected: selectedFormula === 'sum' }"
              @click="setFormula('sum')"
            )
              div(class="text-12px light dark") {{ $t('Sum') }}
            v-list-item(
              class="menu-list__item sm"
              :class="{ selected: selectedFormula === 'mean' }"
              @click="setFormula('mean')"
            )
              div(class="text-12px light dark") {{ $t('Average value') }}
        div(class="mt-2")
          div(
            v-for="field in editableNumericFields"
            :key="field.id"
            class="dynamic-formula__options-item"
          )
            div(class="text-12px light dark") {{ $t(field.hint) }}
            button(
              class="dynamic-formula__options-btn"
              @click="removeField(field.id)"
            )
              e-svg-icon circle-close
    div(
      v-if="showErrors"
      class="dynamic-formula__error"
    ) {{ errorText }}
</template>

<script>
import { mapGetters } from 'vuex'
import ESvgIcon from '~/components/elements/icons/e-svg-icon'
import responsive from '~/mixins/pages/responsive'
import { TYPES } from '~/modules/templates/constants/fields'

export default {
  name: 'TemplatesDynamicFormula',
  components: {
    ESvgIcon
  },
  mixins: [responsive],
  props: {
    field: {
      type: Object,
      default: () => ({})
    },
    fontSize: {
      type: [String, Number],
      default: null
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    canEdit: {
      type: Boolean,
      default: false
    },
    hasError: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      selectedFormula: null,
      editableNumericFields: [],
      showErrors: false,
      deletedFieldIds: []
    }
  },
  computed: {
    ...mapGetters('templatefields', {
      fields: 'fields',
      toolbarsMap: 'toolbarsMap',
      filledFields: 'filledFields'
    }),
    // TODO: approach fields guide another way
    isFilled() {
      return true
    },
    hasErrors() {
      return !this.editableNumericFields.length || !this.selectedFormula
    },
    formulaButtonClasses() {
      return {
        filled: this.selectedFormula,
        opened: this.toolbarsMap[this.field.id],
        'error-state': this.showErrors || this.hasError
      }
    },
    formulaTextStyles() {
      return {
        fontSize: this.xsDevice ? '7px' : this.fontSize + 'px'
      }
    },
    numericFields() {
      return this._.filter(this.fields, field => field.type === TYPES.numeric)
    },
    selectFormulaButtonText() {
      const formulaText = this._.upperFirst(this.selectedFormula)
      return this.selectedFormula ? this.$t(formulaText) : this.$t('Select formula')
    },
    selectedFormulaText() {
      if (this.hasAllNumericFieldsFilled && !this.canEdit) {
        return this.countByFormula(this.selectedFormula)
      }
      if (this.selectedFormula === 'mean') {
        let res = this.$t('Average value') + ': '
        const operands = this._.filter(this.numericFields, item => {
          const operandIds = this._.map(this.operands, item => item.id)
          return operandIds.includes(item.id)
        })
        for (const fieldIdx in operands) {
          res += this.$t(operands[fieldIdx]?.hint)
          const lastItemIdx = operands.length - 1
          if (lastItemIdx !== Number(fieldIdx)) {
            res += ', '
          }
        }
        return res
      }
      if (this.selectedFormula) {
        let res = ''
        const operands = this._.filter(this.numericFields, item => {
          const operandIds = this._.map(this.operands, item => item.id)
          return operandIds.includes(item.id)
        })
        for (const fieldIdx in operands) {
          res += this.$t(operands[fieldIdx]?.hint)
          const lastItemIdx = operands.length - 1
          if (lastItemIdx !== Number(fieldIdx)) {
            res += ' + '
          }
        }
        return res
      }
      return this.$t('Formula')
    },
    operands() {
      return this._.get(this.field, 'options.operands', [])
    },
    hasAllNumericFieldsFilled() {
      if (this.canEdit) {
        return
      }
      return this.operands.every(field => {
        const documentField = this._.find(this.fields, f => f.id === field.id)
        const alreadyHasValue = documentField?.documentFieldValue
        return (
          this.filledFields.some(
            filledField =>
              filledField.id === documentField?.documentFieldId ||
              filledField.id === documentField.id
          ) || alreadyHasValue
        )
      })
    },
    errorText() {
      if (!this.editableNumericFields?.length) {
        return this.$t('Formula should have at least one operand')
      }
      if (!this.selectedFormula) {
        return this.$t('Choose a calculation formula')
      }
      return null
    }
  },
  watch: {
    hasAllNumericFieldsFilled(val) {
      if (!this.canEdit) {
        if (!val) {
          this.$emit('update:value', null)
        }
      }
    },
    numericFields: {
      deep: true,
      handler(val, prev) {
        if (this.canEdit) {
          if (val.length !== prev.length) {
            const clonedNumerics = this._.cloneDeep(val)
            this.editableNumericFields = this._.filter(
              clonedNumerics,
              item => !this.deletedFieldIds.includes(item.id)
            )
            this.updateModel()
          }
        }
      }
    },
    hasErrors(val) {
      if (!val) {
        this.$store.commit('templatefields/REMOVE_ERROR_FIELD_IDS', this.field.key)
        this.showErrors = false
      }
    }
  },
  mounted() {
    this.setFormulaField()
  },
  methods: {
    validate() {
      return new Promise(resolve => {
        this.showErrors =
          !this.editableNumericFields.length || !this.selectedFormula || this.hasError
        if (this.showErrors) {
          this.$store.commit('templatefields/SET_ERROR_FIELD_IDS', this.field.key)
          resolve(false)
        } else {
          resolve(true)
        }
      })
    },
    updateModel() {
      const currentOperation = this._.get(this.field, 'options.operation', null)
      const updatedOptions = {
        operation: currentOperation
      }
      if (this.editableNumericFields.length) {
        updatedOptions.operands = this.editableNumericFields
      }
      this.$store.commit('templatefields/UPDATE_FIELD', {
        id: this.field.id,
        updatedData: {
          options: updatedOptions,
          changed: true
        }
      })
    },
    countByFormula(formula) {
      let result = null
      this._.forEach(this.operands, field => {
        const documentField = this._.find(this.fields, f => f.id === field.id)
        const documentFieldValue = Number(documentField.documentFieldValue)
        const filledOperand =
          this._.find(
            this.filledFields,
            filledField =>
              filledField.id === documentField.documentFieldId ||
              filledField.id === documentField.id
          ) || documentFieldValue
        const operandValue = documentFieldValue || filledOperand.value
        if (filledOperand) {
          result += Number(operandValue)
        }
      })
      if (formula === 'mean') {
        const mean = result / this.operands.length
        result = Number.isInteger(mean) ? mean : mean.toFixed(2)
      }
      this.$emit('update:value', String(result))
      return result
    },
    removeField(id) {
      this.deletedFieldIds.push(id)
      this.editableNumericFields = this._.filter(
        this.editableNumericFields,
        field => field.id !== id
      )
      this.updateModel()
    },
    onMenuOpen() {
      if (this.canEdit) {
        this.$emit('wasOpened', this.field.id)
        this.$store.commit('templatefields/SET_TOOLBARS_MAP_FIELD_ID', {
          id: this.field.id,
          value: true
        })
      }
    },
    setFormula(formula) {
      this.selectedFormula = formula
      this.$store.commit('templatefields/UPDATE_FIELD', {
        id: this.field.id,
        updatedData: {
          options: {
            operation: formula,
            operands: this.editableNumericFields
          },
          changed: true
        }
      })
    },
    setFormulaField() {
      this.selectedFormula = this._.get(this.field, 'options.operation', null)
      const operands = this._.get(this.field, 'options.operands', [])
      if (operands.length) {
        this.editableNumericFields = this._.get(this.field, 'options.operands', [])
      } else {
        this.editableNumericFields = this._.cloneDeep(this.numericFields)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.dynamic-formula {
  &-wrapper {
    position: relative;
    width: 100%;
    height: 100%;
  }

  &__body {
    width: 100%;
  }

  &__activator {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;

    overflow: auto;

    width: 100%;
    height: 100%;

    padding: 5px 8px;

    border: 1px solid $primary;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.7);

    @media (max-width: map-get($breakpoints, 'sm')) {
      border-radius: 3px;
    }

    &.filled {
      background:
        linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
        rgba(255, 255, 255, 0.4);
    }
    &.opened {
      min-width: 220px;
      height: auto;
      background:
        linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
        #fff;

      @media (max-width: map-get($breakpoints, 'sm')) {
        min-width: 110px;
      }
    }
    &.error-state {
      border: 1px solid $danger;
      background: rgba(255, 23, 68, 0.12);
    }
  }

  &__activator-text {
    word-break: break-word;
    text-align: left;
    font-weight: 300;

    @media (max-width: map-get($breakpoints, 'sm')) {
      line-height: 1.5;
    }
  }

  &__menu-btn {
    width: 100%;
    height: 32px;

    padding: 0 4px 0 8px;

    display: flex;
    align-items: center;
    justify-content: space-between;

    border-radius: 8px;
    background: $background-secondary-alternative;

    @media (max-width: map-get($breakpoints, 'sm')) {
      height: 16px;
    }
  }

  &__options-item {
    display: flex;
    align-items: center;
    justify-content: space-between;

    height: 32px;
    padding: 0 6px;

    @media (max-width: map-get($breakpoints, 'sm')) {
      height: 16px;
    }
  }

  &__options-btn {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__error {
    font-size: 12px;
    color: $danger;

    @media (max-width: map-get($breakpoints, 'sm')) {
      font-size: 7px;
    }
  }
}
.selected {
  background:
    linear-gradient(0deg, rgba(143, 37, 125, 0.12) 0%, rgba(143, 37, 125, 0.12)) 100%,
    #fff;
}
</style>
